import { useParams } from '@tanstack/react-router';
import type { CellContext } from '@tanstack/react-table';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Icon from '~/components2/Icon';
import type { ProjectDetailsParams } from '../../..';
import type { UtilityAgency } from '../../common';

const INDENT_PX = 16;

/**
 * CG-611: Agency-name cell that renders the expand/collapse affordance for the root → sub-agency
 * hierarchy. When no row in the table can expand (flag off, or flag on with no split agencies) it
 * renders the plain agency name, byte-for-byte identical to the legacy `accessorKey` output.
 */
export default function AgencyNameCell({ row, getValue, table }: CellContext<UtilityAgency, unknown>) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const name = (getValue() as string) ?? row.original.agencyName;

  // Legacy / no-split path — keep the column pixel-identical to before this feature.
  if (!table.getCanSomeRowsExpand()) {
    return name;
  }

  const canExpand = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  const childCount = row.subRows.length;

  return (
    <div className="flex items-center gap-1" style={{ paddingLeft: row.depth * INDENT_PX }}>
      {canExpand ? (
        <button
          type="button"
          data-testid={`utility-row-expand-${row.original.agencyId}`}
          aria-label={isExpanded ? 'Collapse sub-agencies' : 'Expand sub-agencies'}
          aria-expanded={isExpanded}
          className="flex h-4 w-4 shrink-0 items-center justify-center text-grey-70 hover:text-grey-90"
          onClick={(e) => {
            // Don't let the click bubble to the row handler (which opens the Details view).
            e.stopPropagation();
            row.toggleExpanded();
            capturePosthogEvent(POSTHOG_EVENTS.civilsearchSubAgencyRowToggled, {
              project_id: projectId,
              agency_id: row.original.agencyId,
              sub_row_count: childCount,
              expanded: !isExpanded,
            });
          }}
        >
          <Icon icon={isExpanded ? 'chevronDown' : 'chevronRight'} size={16} />
        </button>
      ) : (
        <span className="inline-block h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      <span className="truncate">{name}</span>
      {canExpand && !isExpanded && (
        <span
          data-testid={`utility-row-subcount-${row.original.agencyId}`}
          className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-grey-20 px-1 text-2xs font-medium text-grey-80"
        >
          {childCount}
        </span>
      )}
    </div>
  );
}
